import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/hooks/useApi";

interface RefundStatusResponse {
  has_pending_request: boolean;
}

export function useRefundRequestStatus(
  purchasedAt: string | null,
  expiresAt: string | null,
) {
  const api = useApi();
  const enabled = purchasedAt != null && expiresAt != null;

  const { data, isLoading } = useQuery({
    queryKey: ["refund-request-status", purchasedAt, expiresAt],
    queryFn: async () => {
      // Normalize timestamps: truncate to seconds to match backend _parse_ts()
      const truncateToSeconds = (d: Date) => new Date(Math.floor(d.getTime() / 1000) * 1000);
      const normalizedPurchased = truncateToSeconds(new Date(purchasedAt!)).toISOString();
      const normalizedExpires = truncateToSeconds(new Date(expiresAt!)).toISOString();

      const params = new URLSearchParams({
        purchased_at: normalizedPurchased,
        expires_at: normalizedExpires,
      });
      const { data: result } = await api.get<RefundStatusResponse>(
        `/boost/refund-request/status?${params.toString()}`,
      );
      return result;
    },
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    hasPendingRequest: data?.has_pending_request ?? false,
    isLoading: enabled && isLoading,
  };
}
